import { CognitoUser } from "@aws-amplify/auth";
import { Auth } from "aws-amplify";
import { Mail, Shield, Smartphone } from "lucide-react";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { resolveIdentityId } from "utils/identity";
import { MfaSetup } from "components/user/mfa-setup";
import ChangePasswordModal from "./change-password-modal";
import { DeleteConfirmationModel } from "./delete-confirmation";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { StandardPageLayout } from "@/components/layouts/standard-page-layout";

export type MfaMethodType =
  | "NOMFA"
  | "TOTP"
  | "SMS"
  | "SMS_MFA"
  | "SOFTWARE_TOKEN_MFA";

export const UserSecurity = () => {
  const [user, setUser] = useState<CognitoUser | null>(null);
  const [mfaEnabled, setMfaEnabled] = useState(false);

  const [totpEnabled, setTotpEnabled] = useState(false);
  const [smsEnabled, setSmsEnabled] = useState(false);

  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const [open, setOpen] = useState(false);

  const [smsDeleteModalOpen, setSmsDeleteModalOpen] = useState(false);
  const [totpDeleteModalOpen, setTotpDeleteModalOpen] = useState(false);

  const [startFromStep, setStartFromStep] = useState(0);

  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const queryClient = useQueryClient();

  const authenticatedUserQuery = useQuery({
    queryKey: [QueryKeys.authenticatedUser, identityId],
    queryFn: () =>
      Auth.currentAuthenticatedUser({
        bypassCache: true,
      }),
  });

  useEffect(() => {
    if (authenticatedUserQuery.isSuccess && authenticatedUserQuery.data) {
      setUser(authenticatedUserQuery.data);
      authenticatedUserQuery.data.getUserData((error: any, userData: any) => {
        if (userData["UserMFASettingList"]) {
          setTotpEnabled(
            userData["UserMFASettingList"].includes("SOFTWARE_TOKEN_MFA"),
          );
          setSmsEnabled(userData["UserMFASettingList"].includes("SMS_MFA"));
        }
      });
    }
  }, [authenticatedUserQuery.data]);

  const setPreferredMFAMutation = useMutation({
    mutationFn: (mfaType: MfaMethodType) => Auth.setPreferredMFA(user, mfaType),
    onSuccess: async () => {
      await invalidateQueries();
    },
  });

  const preferredMfaQuery = useQuery({
    queryKey: [QueryKeys.preferredMfa, identityId],
    queryFn: () =>
      Auth.getPreferredMFA(user, {
        bypassCache: true,
      }),
    enabled: !!user,
  });

  useEffect(() => {
    if (preferredMfaQuery.isSuccess && preferredMfaQuery.data) {
      setMfaEnabled(preferredMfaQuery.data !== "NOMFA");
      if (preferredMfaQuery.data === "NOMFA") {
        setSmsEnabled(false);
        setTotpEnabled(false);
      }
    }
  }, [preferredMfaQuery.isSuccess]);

  const invalidateQueries = async () => {
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.preferredMfa, identityId],
    });
    await queryClient.invalidateQueries({
      queryKey: [QueryKeys.authenticatedUser, identityId],
    });
  };

  const isLoading =
    authenticatedUserQuery.isPending ||
    (preferredMfaQuery.isEnabled && preferredMfaQuery.isPending);
  const isError = authenticatedUserQuery.isError || preferredMfaQuery.isError;
  const isSuccess =
    authenticatedUserQuery.isSuccess && preferredMfaQuery.isSuccess;

  const handleOpen = async () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  return (
    <StandardPageLayout
      pageTitle={{
        icon: Shield,
        title: "User Security",
        description:
          "Manage your password and multi-factor authentication settings.",
      }}
    >
      <div className="mx-auto max-w-5xl space-y-4">
        {/* Password */}
        {isSuccess && (
          <Card>
            <CardHeader>
              <CardTitle>Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="space-y-1">
                <Label htmlFor="password">Current Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value="*****************"
                  readOnly
                  disabled
                />
              </div>
            </CardContent>
            <CardFooter className="justify-end">
              <Button onClick={() => setChangePasswordOpen(true)}>
                Change Password
              </Button>
            </CardFooter>
          </Card>
        )}
        {isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        )}
        <ChangePasswordModal
          open={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
        />
      </div>

      <div className="mx-auto max-w-5xl space-y-4">
        {/* MFA */}
        {isSuccess && (
          <Card>
            <CardHeader>
              <CardTitle>Multi Factor Authentication</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <Label>Preferred MFA method</Label>
                <Select
                  disabled={setPreferredMFAMutation.isPending || !mfaEnabled}
                  value={preferredMfaQuery.data}
                  onValueChange={async (value) => {
                    await setPreferredMFAMutation.mutateAsync(
                      value as MfaMethodType,
                    );
                    await queryClient.invalidateQueries({
                      queryKey: [QueryKeys.preferredMfa, identityId],
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select MFA method" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem
                      value="SOFTWARE_TOKEN_MFA"
                      disabled={!totpEnabled}
                    >
                      Authenticator App (TOTP)
                    </SelectItem>
                    <SelectItem value="SMS_MFA" disabled={!smsEnabled}>
                      SMS/Text Message
                    </SelectItem>
                    <SelectItem value="NOMFA" disabled>
                      No Multi Factor Authentication
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-3">
                <p className="text-sm font-medium">Methods</p>

                <div className="flex items-center gap-2">
                  <Smartphone className="text-muted-foreground size-4" />
                  <span className="text-sm">Authenticator App (TOTP)</span>
                  {totpEnabled && (
                    <Badge
                      variant="outline"
                      className="text-success border-success"
                    >
                      Configured
                    </Badge>
                  )}
                  <div className="ml-auto">
                    {totpEnabled ? (
                      <Button
                        variant="destructive"
                        disabled={isLoading}
                        onClick={() => setTotpDeleteModalOpen(true)}
                      >
                        {isLoading && <Spinner />}
                        Delete
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          setStartFromStep(2);
                          handleOpen();
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Mail className="text-muted-foreground size-4" />
                  <span className="text-sm">SMS/Text Message</span>
                  {smsEnabled && (
                    <Badge
                      variant="outline"
                      className="text-success border-success"
                    >
                      Configured
                    </Badge>
                  )}
                  <div className="ml-auto">
                    {smsEnabled ? (
                      <Button
                        variant="destructive"
                        disabled={isLoading}
                        onClick={() => setSmsDeleteModalOpen(true)}
                      >
                        {isLoading && <Spinner />}
                        Delete
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          setStartFromStep(-2);
                          handleOpen();
                        }}
                      >
                        Add
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {!mfaEnabled && (
                <Alert variant="warning">
                  <AlertDescription>
                    Multi Factor Authentication is not configured on your
                    profile
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        )}

        {isLoading && (
          <Card>
            <CardHeader>
              <CardTitle>Multi Factor Authentication</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-20 w-full" />
            </CardContent>
          </Card>
        )}

        {isError && (
          <Card>
            <CardHeader>
              <CardTitle>Multi Factor Authentication</CardTitle>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive">
                <AlertDescription>
                  An error occured while getting the MFA settings.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}

        <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
          <DialogContent className="p-0">
            {user && (
              <MfaSetup
                cognitoUser={user}
                onOptOut={handleClose}
                onSubmit={async () => {
                  handleClose();
                  setMfaEnabled(true);
                  invalidateQueries();
                  setStartFromStep(0);
                }}
                startFromStep={startFromStep}
              />
            )}
          </DialogContent>
        </Dialog>

        <DeleteConfirmationModel
          open={smsDeleteModalOpen}
          onClose={() => setSmsDeleteModalOpen(false)}
          onDelete={async () => {
            user?.setUserMfaPreference(
              {
                PreferredMfa: false,
                Enabled: false,
              },
              null,
              (error, cb) => {},
            );
            await setPreferredMFAMutation.mutateAsync(
              totpEnabled ? "SOFTWARE_TOKEN_MFA" : "NOMFA",
            );
            invalidateQueries();
            setSmsEnabled(false);
            setSmsDeleteModalOpen(false);
          }}
        />
        <DeleteConfirmationModel
          open={totpDeleteModalOpen}
          onClose={() => setTotpDeleteModalOpen(false)}
          onDelete={async () => {
            user?.setUserMfaPreference(
              null,
              {
                PreferredMfa: false,
                Enabled: false,
              },
              (error, cb) => {},
            );
            invalidateQueries();
            await setPreferredMFAMutation.mutateAsync(
              smsEnabled ? "SMS_MFA" : "NOMFA",
            );
            await invalidateQueries();
            setTotpEnabled(false);
            setTotpDeleteModalOpen(false);
          }}
        />
      </div>
    </StandardPageLayout>
  );
};
